import { Inject, Injectable } from '@angular/core';
import { map, publishReplay, refCount, switchMap } from 'rxjs/operators';
import { Observable } from 'rxjs';
import {
  GetSubscriptionTierRequest,
  ListSubscriptionTiersRequest,
  SubscriptionTierInterface,
  SubscriptionTiersApiService,
} from '@vendasta/partner';
import { FREE_SUBSCRIPTION_ID } from './constants';
import { ListSubscriptionTiersResponse } from '@vendasta/partner/lib/_internal/objects';

@Injectable({ providedIn: 'root' })
export class SubscriptionTiersService {
  public subscriptionTierList$: Observable<SubscriptionTierInterface[]>;

  constructor(
    private api: SubscriptionTiersApiService,
    @Inject('PARTNER_ID') private readonly partnerId$: Observable<string>,
  ) {
    this.subscriptionTierList$ = partnerId$.pipe(
      switchMap((partnerId) => {
        return this.listSubscriptionTiers(partnerId);
      }),
      map((response) => response?.subscriptionTier),
      publishReplay(1),
      refCount(),
    );
  }

  // Returns a string like 'SUBSCRIPTION_TIERS.basic_subscription.NAME' to be used with frontend translate pipe.
  public static getI18NName(id: string): string {
    return `SUBSCRIPTION_TIERS.${id}.NAME`;
  }

  public static isFree(s: SubscriptionTierInterface): boolean {
    return s.id === FREE_SUBSCRIPTION_ID;
  }

  public static isClassicSubscription(s: SubscriptionTierInterface): boolean {
    return s.tierVersion === 'Classic';
  }

  public static isVBP2Subscription(s: SubscriptionTierInterface): boolean {
    return s.tierVersion === 'VBP 2.0';
  }

  public static isVBP2022Subscription(s: SubscriptionTierInterface): boolean {
    return s.tierVersion === 'VBP2022';
  }

  public static isM3Subscription(s: SubscriptionTierInterface): boolean {
    return s.tierVersion === 'M3';
  }

  public getSubscriptionTier(subscriptionId: string, partnerId: string): Observable<SubscriptionTierInterface> {
    return this.api
      .getSubscriptionTier(new GetSubscriptionTierRequest({ id: subscriptionId, partnerId: partnerId }))
      .pipe(map((response) => response.subscriptionTier));
  }

  // use public subscriptionTierList$ to get list of subscriptions
  private listSubscriptionTiers(partnerId: string): Observable<ListSubscriptionTiersResponse> {
    const request = new ListSubscriptionTiersRequest({ partnerId: partnerId });
    return this.api.listSubscriptionTiers(request);
  }
}
